import { Link } from 'react-router-dom';

import { api } from '../../api/endpoints.js';
import { Badge, Card, DataTable, Input, Pagination, Select } from '../../components/ui.jsx';
import { useListQuery } from '../../hooks/useListQuery.js';
import { dateNumeric, money, number } from '../../lib/format.js';

/** Days until a date, or a negative number once it has passed. */
function daysUntil(value) {
  if (!value) return null;
  return Math.round((new Date(value).getTime() - Date.now()) / 86_400_000);
}

/**
 * Where an ad stands, as the one thing worth knowing about it.
 *
 * A paid period, a lapsed renewal or nothing paid at all — three states that a
 * date alone does not distinguish, because "yesterday" means lapsed and
 * "never" means the listing was never advertised.
 */
function adState(property) {
  if (property.ad?.lapsedAt) return { tone: 'danger', label: 'Lapsed' };
  if (!property.ad?.paidUntil) return { tone: 'neutral', label: 'Not advertised' };

  const days = daysUntil(property.ad.paidUntil);
  if (days < 0) return { tone: 'warning', label: 'Due' };
  if (days <= 3) return { tone: 'warning', label: `Renews in ${days}d` };
  return { tone: 'success', label: `Renews in ${days}d` };
}

export default function AdsTab() {
  const list = useListQuery({
    key: 'admin-ads',
    fetcher: api.ads.list,
    initialFilters: { status: '', lapsed: '', boosted: '' },
    searchable: true,
  });

  const columns = [
    {
      key: 'listing',
      header: 'Listing',
      render: (property) => (
        <div>
          <div className="table__primary">{property.title}</div>
          <div className="table__muted">
            {[property.address?.locality, property.address?.city].filter(Boolean).join(', ')}
          </div>
        </div>
      ),
    },
    {
      key: 'lister',
      header: 'Paid by',
      render: (property) => (
        <div>
          {property.broker ? (
            <Link className="table__link" to={`/brokers/${property.broker._id}`}>
              {property.broker.businessName}
            </Link>
          ) : (
            <div>{property.brokerUser?.name || 'Landlord'}</div>
          )}
          <div className="table__muted">
            {property.broker ? 'Agency' : 'Own home'}
            {property.brokerUser ? ` · ${number(property.brokerUser.creditBalance)} credits` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'rent',
      header: 'Rent',
      className: 'table__numeric',
      render: (property) => (
        <span className="nowrap">
          {money(property.rentMinor)}
          <span className="muted">/mo</span>
        </span>
      ),
    },
    { key: 'status', header: 'Listing', render: (property) => <Badge status={property.status} /> },
    {
      key: 'ad',
      header: 'Ad',
      render: (property) => {
        const state = adState(property);

        return (
          <div>
            <Badge tone={state.tone} label={state.label} />
            {property.ad?.paidUntil ? (
              <div className="table__muted">{dateNumeric(property.ad.paidUntil)}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'boost',
      header: 'Boost',
      render: (property) =>
        property.boost?.boostedUntil && new Date(property.boost.boostedUntil) > new Date() ? (
          <Badge tone="info" label={`Until ${dateNumeric(property.boost.boostedUntil)}`} />
        ) : (
          <span className="table__muted">—</span>
        ),
    },
    {
      key: 'cost',
      header: 'Per period',
      className: 'table__numeric',
      render: (property) =>
        property.ad?.creditCost == null ? (
          <span className="table__muted">—</span>
        ) : (
          <span className="nowrap">{number(property.ad.creditCost)} cr</span>
        ),
    },
  ];

  return (
    <Card bodyless>
      <div className="card__body">
        <div className="toolbar">
          <div className="toolbar__search">
            <Input
              placeholder="Search by title or city"
              value={list.search}
              onChange={(event) => list.setSearch(event.target.value)}
              aria-label="Search ads"
            />
          </div>

          <Select
            value={list.filters.status}
            onChange={(event) => list.setFilter('status', event.target.value)}
            aria-label="Filter by listing status"
          >
            <option value="">Any listing status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="rented">Rented</option>
            <option value="archived">Archived</option>
          </Select>

          <Select
            value={list.filters.lapsed}
            onChange={(event) => list.setFilter('lapsed', event.target.value)}
            aria-label="Filter by whether the ad lapsed"
          >
            <option value="">Paid and lapsed</option>
            <option value="true">Lapsed only</option>
          </Select>

          <Select
            value={list.filters.boosted}
            onChange={(event) => list.setFilter('boosted', event.target.value)}
            aria-label="Filter by boost"
          >
            <option value="">Boosted and not</option>
            <option value="true">Boosted only</option>
          </Select>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={list.rows}
        query={list.query}
        empty={{
          title: 'No ads here',
          body: 'No listing matches these filters.',
        }}
      />
      <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
    </Card>
  );
}
